import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sparkles, ArrowRight, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Phase 7B — P1.1 Commercial Offers Banner.
 *
 * Fetches `GET /api/offers/my-active` on mount + on user change. Shows
 * the top-priority active offer (backend enforces window + eligibility
 * → no fake urgency). Click "Claim" calls `POST /offers/{id}/claim`
 * to obtain a Stripe coupon_code, then invokes the subscription-checkout
 * endpoint with that coupon and redirects to Stripe hosted checkout.
 *
 * The banner is dismissable per-session (localStorage per offer id).
 */
export default function CommercialOfferBanner() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [offer, setOffer] = useState(null);
    const [busy, setBusy] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    const load = useCallback(async () => {
        if (!user) return;
        try {
            const res = await apiFetch("/offers/my-active");
            const first = (res && res.offers && res.offers[0]) || null;
            setOffer(first);
            if (first) {
                const key = `glms_offer_dismissed_${first.id}`;
                if (typeof window !== "undefined" && window.localStorage.getItem(key)) {
                    setDismissed(true);
                } else {
                    setDismissed(false);
                }
            }
        } catch (e) {
            // silent — offers are an enhancement, not a blocker
        }
    }, [user]);

    useEffect(() => { load(); }, [load]);

    const claim = async () => {
        if (!offer || busy) return;
        setBusy(true);
        try {
            const claimRes = await apiFetch(`/offers/${offer.id}/claim`, {
                method: "POST",
            });
            const checkoutRes = await apiFetch("/billing/subscription-checkout", {
                method: "POST",
                body: JSON.stringify({
                    plan_id: claimRes.target_plan_id || "pro",
                    interval: claimRes.target_interval || "monthly",
                    coupon_code: claimRes.coupon_code,
                }),
            });
            if (checkoutRes && checkoutRes.url) {
                window.location.href = checkoutRes.url;
                return;
            }
            toast.error("Checkout unavailable, please retry.");
        } catch (e) {
            toast.error(e.message || "Offer claim failed");
        } finally {
            setBusy(false);
        }
    };

    const dismiss = () => {
        if (!offer) return;
        setDismissed(true);
        if (typeof window !== "undefined") {
            window.localStorage.setItem(`glms_offer_dismissed_${offer.id}`, "1");
        }
    };

    if (!offer || dismissed) return null;

    // Copy resolution — deliberately real numbers: discount% and hours are
    // whatever the backend enforces, we do NOT hard-code any urgency here.
    const title = t(`offers.${offer.offer_type}.title`, {
        discount: offer.discount_percentage,
    });
    const body = t(`offers.${offer.offer_type}.body`, {
        discount: offer.discount_percentage,
        months: offer.duration_in_months || 3,
        hours: offer.hours_remaining,
    });

    return (
        <AnimatePresence>
            <motion.div
                key={offer.id}
                initial={{ opacity: 0, y: -12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25 }}
                data-testid="commercial-offer-banner"
                className="mb-6 rounded-2xl border border-[var(--color-primary)]/30 bg-gradient-to-br from-[var(--color-primary)]/10 via-transparent to-[var(--color-secondary)]/10 p-5 flex items-start gap-4"
            >
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white hover:bg-[#EDEDED] shrink-0">
                    <Sparkles className="h-4 w-4 text-white" />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] uppercase tracking-widest font-semibold text-[var(--color-secondary)]">
                            {t("offers.limited_label")}
                        </span>
                        <span
                            data-testid="commercial-offer-hours"
                            className="text-[10px] text-text-secondary tabular-nums"
                        >
                            · {t("offers.hours_remaining", { hours: offer.hours_remaining })}
                        </span>
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-text-main">{title}</h3>
                    <p className="text-xs md:text-sm text-text-secondary mt-1 max-w-2xl">{body}</p>
                    <div className="mt-3 flex items-center gap-3">
                        <button
                            onClick={claim}
                            disabled={busy}
                            data-testid="commercial-offer-claim"
                            className="text-xs md:text-sm rounded-full bg-white hover:bg-[#EDEDED] text-black px-5 py-2 font-medium hover:opacity-90 flex items-center gap-1.5 disabled:opacity-60"
                        >
                            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                            {t("offers.claim_cta")}
                            {!busy && <ArrowRight className="h-3.5 w-3.5" />}
                        </button>
                        <button
                            onClick={dismiss}
                            data-testid="commercial-offer-dismiss"
                            className="text-xs text-text-secondary hover:text-text-main transition-colors"
                        >
                            {t("offers.dismiss")}
                        </button>
                    </div>
                </div>
                <button
                    onClick={dismiss}
                    aria-label="Close"
                    data-testid="commercial-offer-close"
                    className="text-text-secondary hover:text-text-main transition-colors self-start"
                >
                    <X className="h-4 w-4" />
                </button>
            </motion.div>
        </AnimatePresence>
    );
}
